import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';
import type { MaterialUnit } from './material-items.api';

export type MaterialActionType = 'purchase' | 'return';
export type MaterialRequestStatus = 'pending' | 'approved' | 'rejected';

export interface MaterialRequestItem {
  id: string;
  materialItemId: string;
  materialItem?: { id: string; name: string };
  qty: string;
  unit: MaterialUnit;
}

export interface MaterialRequest {
  id: string;
  schoolId: string;
  school?: { id: string; name: string };
  actionType: MaterialActionType;
  status: MaterialRequestStatus;
  notes: string | null;
  sessionYear: string;
  items: MaterialRequestItem[];
  createdByUserId: string | null;
  createdByName?: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MaterialRequestListParams {
  page?: number;
  limit?: number;
  search?: string;
  schoolId?: string;
  status?: MaterialRequestStatus;
  sessionYear?: string;
}

export interface CreateMaterialRequestPayload {
  actionType: MaterialActionType;
  notes?: string;
  items: { materialItemId: string; qty: number }[];
  /** Org Admin only — which of their schools this request is for. */
  schoolId?: string;
}

/** Never includes status — that stays on approve()/reject(). */
export interface UpdateMaterialRequestPayload {
  actionType?: MaterialActionType;
  notes?: string;
  items?: { materialItemId: string; qty: number }[];
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export const MaterialRequestsApi = {
  list: async (params?: MaterialRequestListParams): Promise<PaginatedResult<MaterialRequest>> =>
    unwrap(await api.get('/material-requests', { params })),
  get: async (id: string): Promise<MaterialRequest> => unwrap(await api.get(`/material-requests/${id}`)),
  create: async (data: CreateMaterialRequestPayload): Promise<MaterialRequest> =>
    unwrap(await api.post('/material-requests', data)),
  update: async (id: string, data: UpdateMaterialRequestPayload): Promise<MaterialRequest> =>
    unwrap(await api.patch(`/material-requests/${id}`, data)),
  approve: async (id: string): Promise<MaterialRequest> =>
    unwrap(await api.patch(`/material-requests/${id}/approve`)),
  reject: async (id: string): Promise<MaterialRequest> =>
    unwrap(await api.patch(`/material-requests/${id}/reject`)),
  remove: async (id: string): Promise<{ id: string }> => unwrap(await api.delete(`/material-requests/${id}`)),
};
